import React, { useState, forwardRef } from 'react'
import { styled } from 'baseui'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import { lightTheme } from '@/customTheme'
import { CARD_CHECKER_BACKGROUND } from '@/components/GeneratedCard/GeneratedCard.styles'
import emptyFramBG from '@assets/images/empty_frame_bg.svg'
import Loading from '@/components/Loading'

export type SquareImageCardBackground = 'frame' | 'checker' | 'none'

interface CardProps {
  $padding: string
  style?: any
  [key: string]: any
}

const Card = styled('div', ({ $padding, style }: CardProps) => ({
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: $padding,
  borderRadius: '16px',
  backgroundColor: lightTheme.colors.grayScale50,
  overflow: 'hidden',
  ...style,
}))

export interface SquareImageCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> {
  src?: string | null
  alt?: string
  background?: SquareImageCardBackground
  padding?: string
  isLoading?: boolean
  /** Set false for already-in-memory sources (e.g. data URLs) to skip the
   * intersection-observer/blur-in delay — there's nothing to lazily fetch. */
  lazy?: boolean
  fallback?: React.ReactNode
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void
  onImageError?: (event: React.SyntheticEvent<HTMLImageElement>) => void
  /** Baseui `styled` allows nested pseudo-selectors (e.g. ':hover') here, wider than plain CSSProperties. */
  style?: any
}

const SquareImageCard = forwardRef<HTMLDivElement, SquareImageCardProps>(function SquareImageCard(
  {
    src,
    alt = '',
    background = 'frame',
    padding = '24px',
    isLoading,
    lazy = true,
    fallback,
    onImageLoad,
    onImageError,
    style,
    children,
    ...rest
  },
  ref,
) {
  const [shouldFillWidth, setShouldFillWidth] = useState(true)

  const handleLoad = (event?: React.SyntheticEvent<HTMLImageElement>) => {
    const target = event?.target as HTMLImageElement | null
    if (target?.naturalWidth && target?.naturalHeight) {
      setShouldFillWidth(target.naturalWidth >= target.naturalHeight)
    }
    onImageLoad?.(event)
  }

  const thumbnailSizing = shouldFillWidth
    ? { width: '100%', height: 'auto' }
    : { width: 'auto', height: '100%' }

  const backgroundStyle =
    background === 'frame'
      ? { backgroundImage: `url(${emptyFramBG})` }
      : background === 'checker'
        ? { background: CARD_CHECKER_BACKGROUND }
        : {}

  return (
    <Card ref={ref} $padding={padding} style={style} {...rest}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          maxWidth: 'fit-content',
          maxHeight: 'fit-content',
        }}
      >
        {src ? (
          <div
            style={{
              display: 'flex',
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LazyLoadImage
              effect="blur"
              visibleByDefault={!lazy}
              src={src}
              alt={alt}
              draggable={false}
              onLoad={handleLoad}
              onError={onImageError}
              style={{
                borderRadius: '6px',
                boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.12)',
                objectFit: 'contain',
                ...backgroundStyle,
                ...thumbnailSizing,
              }}
              wrapperProps={{
                style: {
                  maxWidth: 'fit-content',
                  maxHeight: 'fit-content',
                  objectFit: 'contain',
                  ...thumbnailSizing,
                },
              }}
            />
          </div>
        ) : (
          fallback
        )}

        {isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.4)',
              borderRadius: '6px',
            }}
          >
            <Loading />
          </div>
        )}
      </div>

      {children}
    </Card>
  )
})

export default SquareImageCard
